import React, { useCallback, useState } from 'react'

import { HiChevronUpDown } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import { TEXT_INPUT_CLASSNAME } from '@/components/common/TextInputV2'
import { getComboboxTriggerClassName } from '@/components/library-v2/ui/Combobox/lib'

import type { UUID } from '@/types/Id'
import type { EntityCompanyDepartment } from '@/types/library/Entity'

interface DepartmentComboboxProps {
  value: UUID | null
  onChange: (departmentId: UUID) => void
  onClear?: () => void
  departments?: EntityCompanyDepartment[]
  placeholder?: string
  disabled?: boolean
}

interface DepartmentItemProps {
  department: EntityCompanyDepartment
}

const DepartmentItem: React.FC<DepartmentItemProps> = ({ department }) => {
  return (
    <div className="flex items-center gap-2 truncate">
      <span className="text-sm truncate">{department.name}</span>
    </div>
  )
}

export const DepartmentCombobox: React.FC<DepartmentComboboxProps> = ({
  value,
  onChange,
  onClear,
  departments,
  placeholder = 'Select department...',
  disabled = false,
}) => {
  const [open, setOpen] = useState<boolean>(false)

  const handleSelect = useCallback(
    (deptId: UUID) => () => {
      onChange(deptId)
      setOpen(false)
    },
    [onChange, setOpen],
  )

  const handleClear = useCallback(() => {
    onClear?.()
    setOpen(false)
  }, [onClear, setOpen])

  const selectedDepartment = departments?.find((d) => d.id === value)

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={twMerge(
            TEXT_INPUT_CLASSNAME,
            getComboboxTriggerClassName({ disabled }),
          )}
        >
          {selectedDepartment ? (
            <DepartmentItem department={selectedDepartment} />
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
          <HiChevronUpDown size={16} className="flex-shrink-0" />
        </button>
      </ComboboxTrigger>
      <ComboboxContent align="start" className="p-0 w-[320px]">
        <ComboboxInput
          placeholder="Search departments..."
          className="placeholder:text-sm"
        />
        <ComboboxList>
          <ComboboxEmpty className="text-sm">
            No departments found.
          </ComboboxEmpty>
          <ComboboxGroup>
            {value && onClear ? (
              <ComboboxSelectableItem
                key="__clear__"
                value="__clear__"
                isSelected={false}
                onSelect={handleClear}
              >
                <span className="text-sm text-red-600 dark:text-red-400">
                  Clear selection
                </span>
              </ComboboxSelectableItem>
            ) : null}
            {(departments ?? []).map((dept) => {
              return (
                <ComboboxSelectableItem
                  key={dept.id}
                  value={`${dept.name} - ${dept.id}`}
                  isSelected={value === dept.id}
                  onSelect={handleSelect(dept.id)}
                >
                  <DepartmentItem department={dept} />
                </ComboboxSelectableItem>
              )
            })}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

export const DepartmentSelect: React.FC<DepartmentComboboxProps> = ({
  value,
  onChange,
  departments,
  placeholder = 'Select department...',
  disabled = false,
}) => {
  const handleValueChange = useCallback(
    (deptId: string) => {
      onChange(deptId as UUID)
    },
    [onChange],
  )

  const selectedDepartment = departments?.find((d) => d.id === value)

  return (
    <Select
      value={value || ''}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-full dark:bg-default-dark">
        <SelectValue placeholder={placeholder}>
          {selectedDepartment ? (
            <span className="text-sm truncate">{selectedDepartment.name}</span>
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {departments && departments.length > 0 ? (
          departments.map((dept) => (
            <SelectItem key={dept.id} value={dept.id}>
              <span className="text-sm truncate">{dept.name}</span>
            </SelectItem>
          ))
        ) : (
          <div className="text-sm truncate px-2 py-[5px] text-primary-500 dark:text-primary-400">
            <span className="text-sm truncate">No departments found</span>
          </div>
        )}
      </SelectContent>
    </Select>
  )
}
